import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Backup as BackupIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  History as HistoryIcon,
  Replay as RestoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { backupAPI } from '../../services/api';

dayjs.extend(relativeTime);

const defaultResourceTypes = ['cases', 'histories', 'attachments'];

const formatDate = (value) => {
  if (!value) return '—';
  return `${dayjs(value).format('YYYY-MM-DD HH:mm')} (${dayjs(value).fromNow()})`;
};

const JobStatusChip = ({ status }) => {
  const color = {
    succeeded: 'success',
    running: 'info',
    queued: 'warning',
    failed: 'error'
  }[status] || 'default';
  return <Chip size="small" label={status} color={color} />;
};

const BackupManager = () => {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [exportJobs, setExportJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [resourceTypes, setResourceTypes] = useState(defaultResourceTypes);
  const [alerts, setAlerts] = useState([]);

  const pushAlert = useCallback((message, severity = 'info') => {
    setAlerts((prev) => [...prev.slice(-2), { id: Date.now(), message, severity }]);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusResponse, jobResponse, exportResponse] = await Promise.all([
        backupAPI.getStatus(),
        backupAPI.listJobs(),
        backupAPI.listExports()
      ]);
      setStatus(statusResponse.data);
      setJobs(jobResponse.data || []);
      setExportJobs(exportResponse.data || []);
    } catch (error) {
      pushAlert(error.response?.data?.message || 'Failed to load backup status', 'error');
    } finally {
      setLoading(false);
    }
  }, [pushAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleManualBackup = async () => {
    setLoading(true);
    try {
      await backupAPI.triggerManual({ reason: 'admin-panel-manual' });
      pushAlert('Manual backup queued successfully', 'success');
      await loadData();
    } catch (error) {
      pushAlert(error.response?.data?.message || 'Failed to trigger manual backup', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDrill = async () => {
    setLoading(true);
    try {
      await backupAPI.triggerRestoreDrill({ reason: 'admin-panel-manual' });
      pushAlert('Restore drill kicked off. Monitor the job list for status.', 'info');
      await loadData();
    } catch (error) {
      pushAlert(error.response?.data?.message || 'Failed to run restore drill', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToggle = (type) => {
    setResourceTypes((prev) => {
      const next = prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type];
      return next.length ? next : prev;
    });
  };

  const handleQueueExport = async () => {
    setExportLoading(true);
    try {
      const job = await backupAPI.queueExport({ resourceTypes });
      pushAlert('Export job queued successfully', 'success');
      setExportJobs((prev) => [job.data, ...prev]);
      await loadData();
    } catch (error) {
      pushAlert(error.response?.data?.message || 'Failed to queue export job', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefreshExport = async (jobId) => {
    try {
      const response = await backupAPI.refreshExportDownload(jobId);
      pushAlert('Download link refreshed', 'success');
      window.open(response.data.signedUrl, '_blank');
    } catch (error) {
      pushAlert(error.response?.data?.message || 'Failed to refresh download link', 'error');
    }
  };

  const latestSuccess = status?.latestSuccess;
  const scheduleSummary = useMemo(() => {
    if (!status) return 'Nightly backups pending schedule';
    return `Nightly schedule: ${status.schedule || 'not set'} · Restore drills: ${status.restoreDrillSchedule || 'not set'} · Retention: ${status.retentionDays || '∞'} days`;
  }, [status]);

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            avatar={<BackupIcon color="primary" />}
            title="Backup & Recovery Overview"
            subheader={scheduleSummary}
          />
          <CardContent>
            {alerts.map((alert) => (
              <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            ))}

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h6">Latest Successful Backup</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formatDate(latestSuccess?.completedAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Archive key: {latestSuccess?.archiveKey || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6">Last Failure</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formatDate(status?.latestFailure?.completedAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {status?.latestFailure?.error?.message || 'No failures recorded'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                    onClick={handleManualBackup}
                    disabled={loading}
                  >
                    Run Manual Backup
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RestoreIcon />}
                    onClick={handleRestoreDrill}
                    disabled={loading}
                  >
                    Run Restore Drill
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<RefreshIcon />}
                    onClick={loadData}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            avatar={<HistoryIcon color="primary" />}
            title="Backup Jobs"
            subheader="Nightly, manual, and restore drill executions"
          />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Archive Key</TableCell>
                    <TableCell>Size</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job._id}>
                      <TableCell>{job._id}</TableCell>
                      <TableCell>{job.jobType}</TableCell>
                      <TableCell><JobStatusChip status={job.status} /></TableCell>
                      <TableCell>{formatDate(job.startedAt)}</TableCell>
                      <TableCell>{formatDate(job.completedAt)}</TableCell>
                      <TableCell>{job.archiveKey || '—'}</TableCell>
                      <TableCell>{job.archiveSize ? `${Math.round(job.archiveSize / (1024 * 1024))} MB` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            avatar={<DownloadIcon color="primary" />}
            title="On-Demand Exports"
            subheader="Generate tenant-ready archives with signed download links"
          />
          <Divider />
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <FormControl component="fieldset" variant="standard">
                <Typography variant="subtitle1">Included resources</Typography>
                <FormGroup row>
                  {defaultResourceTypes.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={(
                        <Switch
                          checked={resourceTypes.includes(type)}
                          onChange={() => handleExportToggle(type)}
                          name={type}
                        />
                      )}
                      label={type}
                    />
                  ))}
                </FormGroup>
              </FormControl>
              <Button
                variant="contained"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleQueueExport}
                disabled={exportLoading}
              >
                Queue Export
              </Button>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Resources</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exportJobs.map((job) => (
                    <TableRow key={job._id}>
                      <TableCell>{job._id}</TableCell>
                      <TableCell><JobStatusChip status={job.status} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {job.resourceTypes?.map((type) => (
                            <Chip key={type} size="small" label={type} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{job.exportFormat}</TableCell>
                      <TableCell>{formatDate(job.createdAt)}</TableCell>
                      <TableCell>{formatDate(job.completedAt)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          disabled={job.status !== 'succeeded'}
                          onClick={() => handleRefreshExport(job._id)}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default BackupManager;
